import { useCallback, useMemo, useState } from "react";
import type { FieldDto } from "../types/field";
import type { ValuesByName } from "../types/visibility";
import {
  buildVisibleByName,
  normalizeValue,
  pruneHiddenFieldValues,
} from "../lib/forms/visibility";

export function useFormVisibility(
  allFields: FieldDto[],
  initialValues: ValuesByName = {},
) {
  const [valuesByName, setValuesByName] = useState<ValuesByName>(() =>
    pruneHiddenFieldValues(allFields, initialValues),
  );

  const visibleByName = useMemo(
    () => buildVisibleByName(allFields, valuesByName),
    [allFields, valuesByName],
  );

  const setFieldValue = useCallback(
    (name: string, raw: unknown) => {
      setValuesByName((prev) => {
        const withUpdate: ValuesByName = {
          ...prev,
          [name]: normalizeValue(raw),
        };
        return pruneHiddenFieldValues(allFields, withUpdate);
      });
    },
    [allFields],
  );

  const resetValues = useCallback(() => {
    setValuesByName({});
  }, []);

  return {
    valuesByName,
    visibleByName,
    setFieldValue,
    resetValues,
  };
}
